import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { t } from 'i18next'
import agent from '../api/agent'
import { User, UserFormValues } from '../models/User'
import { store } from './store'
import { history } from '../..'

enum Roles {
  Admin = 'Admin',
  Administrator = 'Administrator',
  Suspended = 'Suspended',
  Moderator = 'Moderator',
}

declare global {
  interface Window {
    gapi: any
  }
}

export default class UserStore {
  user: User | null = null

  googleAccessToken?: string

  fbAccessToken?: string

  userRegistry = new Map<string, User>()

  selectedUser: User | undefined = undefined

  loadingInitial = true

  loginModal = false

  loadingButton = false

  resendConfirmEmail = false

  loading = false

  confirmEmail = false

  editUserModal = false

  registerEmail = ''

  error = ''

  moderators?: string[]

  loadingGoogle = false

  constructor() {
    makeAutoObservable(this)
  }

  get totalUsers() {
    return this.userRegistry.size
  }

  setLoading = (state: boolean) => {
    this.loading = state
  }

  setLoadingGoogle = (state: boolean) => {
    this.loadingGoogle = state
  }

  setEditUserModal = (state: boolean) => {
    this.editUserModal = state
  }

  selectUser = (email: string) => {
    this.selectedUser = this.userRegistry.get(email)
    this.setEditUserModal(true)
  }

  setError = (error: string) => {
    this.error = error
  }

  toggleConfirmEmail = (state: boolean) => {
    this.confirmEmail = state
  }

  toggleResendConfirmEmail = (state: boolean) => {
    this.resendConfirmEmail = state
  }

  setRegisterEmail = (state: string) => {
    this.registerEmail = state
  }

  setLoginModal = (state: boolean) => {
    this.loginModal = state
  }

  setUserHasPassword = (state: boolean) => {
    this.user = this.user ?? ({} as User)
    this.user.hasPassword = state
  }

  get isLoggedIn() {
    return !!this.user
  }

  get userAvatar() {
    return this.getData.avatar
      ? `/images/avatar/avatar${this.getData.avatar}.png`
      : '/images/avatar/avatar1.png'
  }

  get getData() {
    const firstName = this.user?.firstName || 'John'
    const lastName = this.user?.lastName || 'Doe'
    const getDatas = {
      email: this.user?.email,
      firstName,
      lastName,
      phoneNumber: this.user?.phoneNumber,
      gender: this.user?.gender,
      id: this.user?.id,
      avatar: this.user?.avatar,
      companyName: this.user?.companyName,
      companyUrl: this.user?.companyUrl,
    }
    return getDatas
  }

  get isAdmin() {
    return this.user?.userRoles.includes(Roles.Admin)
  }

  get isAdministrator() {
    return this.user?.userRoles.includes(Roles.Administrator)
  }

  get isModerator() {
    return this.user?.userRoles.includes(Roles.Moderator)
  }

  get isSuspended() {
    return this.user?.userRoles.includes(Roles.Suspended)
  }

  get getAllUsers() {
    return Array.from(this.userRegistry.values())
  }

  getUser = async () => {
    const { status, data: user } = await agent.Account.current()
    if (status === 200) {
      runInAction(() => (this.user = user))
      if (this.isSuspended) {
        this.logout()
      }
    }
  }

  getModerators = async () => {
    const { status, data: result } = await agent.Account.getModerators()
    if (status === 200) {
      runInAction(() => {
        this.moderators = result
      })
    }
  }

  loadAllUsers = async () => {
    this.loadingInitial = true
    const { status, data: users } = await agent.Account.list()
    if (status === 200) {
      runInAction(() => {
        users.forEach((user) => {
          this.userRegistry.set(user.email, user)
        })
        this.loadingInitial = false
      })
    } else {
      runInAction(() => {
        this.loadingInitial = false
      })
    }
  }

  login = async (creds: UserFormValues) => {
    const { status, data: user } = await agent.Account.login(creds)
    if (status === 200) {
      store.commonStore.setToken(user.token)
      runInAction(() => {
        this.user = user
      })
    }
  }

  register = async (creds: UserFormValues) => {
    this.loadingButton = true
    try {
      if (creds.password === creds.confirmpassword) {
        await agent.Account.register(creds)
        runInAction(() => {
          this.setLoginModal(false)
          this.confirmEmail = true
          this.loadingButton = false
          this.registerEmail = creds.email
          this.setError('')
        })
      } else {
        runInAction(() => {
          this.loadingButton = false
        })
      }
    } catch (error: any) {
      runInAction(() => {
        this.loadingButton = false
      })
      throw error
    }
  }

  getFacebookLoginStatus = async () => {
    if (!window.FB) {
      await new Promise((resolve: any) => {
        window.fbAsyncInit = function () {
          FB.init({
            appId: '653305173410538',
            cookie: true,
            xfbml: true,
            version: 'v17.0',
          })

          FB.AppEvents.logPageView()

          resolve()
        }

        // Load the Facebook SDK asynchronously
        ;(function (d, s, id) {
          const fjs: any = d.getElementsByTagName(s)[0]
          if (d.getElementById(id)) return
          const js = d.createElement(s) as HTMLImageElement
          js.id = id
          js.src = 'https://connect.facebook.net/en_US/sdk.js'
          fjs.parentNode.insertBefore(js, fjs)
        })(document, 'script', 'facebook-jssdk')
      })
    }

    FB.getLoginStatus((response: any) => {
      if (response.status === 'connected') {
        this.fbAccessToken = response.authResponse.accessToken
      }
    })
  }

  changeUserLanguage = async (language: string) => {
    await agent.Account.changeLanguage({ language })
  }

  getGoogleLoginStatus = async () => {
    // await this.getFacebookLoginStatus()

    if (typeof window.gapi === 'undefined') {
      console.error('Google Sign-In SDK not loaded.')
      return
    }
    window.gapi.load('auth2', () => {
      window.gapi.auth2
        .init({
          scope: 'email',
          // eslint-disable-next-line camelcase
          plugin_name: 'streamy',
        })
        .then((auth2: any) => {
          const googleUser = auth2.currentUser.get()
          if (googleUser.isSignedIn()) {
            const googleIdToken = googleUser.getAuthResponse().id_token
            this.googleAccessToken = googleIdToken
          }
        })
        .catch((error: any) => {
          console.log(error)
        })
    })
  }

  googleLogin = () => {
    // TODO: uncomment code below but needs to be checked user.token as part of response type
    this.setLoadingGoogle(true)
    const apiLogin = (accessToken: string) => {
      agent.Account.loginGoogle(accessToken)
        .then((user) => {
          const params = new URLSearchParams(location.search)
          store.commonStore.setToken(user.data.token)
          runInAction(() => {
            this.user = user.data
          })
          history.push(params.get('redirectTo') || '/')
        })
        .finally(() => this.setLoadingGoogle(false))
    }
    if (this.googleAccessToken) {
      apiLogin(this.googleAccessToken)
    } else {
      window.gapi.auth2
        .getAuthInstance()
        .signIn()
        .catch((error: any) => {
          console.error('Error during Google Sign-In:', error)
          this.setLoadingGoogle(false)
        })
        .then((googleUser: any) => {
          if (googleUser) {
            const googleIdToken = googleUser.getAuthResponse().id_token
            apiLogin(googleIdToken)
          } else {
            this.setLoadingGoogle(false)
          }
        })
    }
  }

  facebookLogin = () => {
    // TODO: uncomment code below but needs to be checked user.token as part of response type
    // this.setLoadingGoogle(true)
    // const apiLogin = (accessToken: string) => {
    //   agent.Account.fbLogin(accessToken)
    //     .then((user) => {
    //       store.commonStore.setToken(user.token)
    //       runInAction(() => {
    //         this.user = user
    //       })
    //       history.push('/')
    //     })
    //     .catch((error) => {
    //       console.log(error)
    //     })
    //     .finally(() => this.setLoadingGoogle(false))
    // }
    // if (this.fbAccessToken) {
    //   apiLogin(this.fbAccessToken)
    // } else {
    //   window.FB.login(
    //     (response: any) => {
    //       if (response.authResponse) {
    //         apiLogin(response.authResponse.accessToken)
    //       } else {
    //         this.setLoadingGoogle(false)
    //       }
    //     },
    //     { scope: 'public_profile,email' },
    //   )
    // }
  }

  logout = async () => {
    if (window.gapi && window.gapi.auth2) {
      const auth2 = window.gapi.auth2.getAuthInstance()
      if (auth2.isSignedIn.get()) {
        await auth2.signOut()
      }
    }
    this.googleAccessToken = undefined
    store.commonStore.setToken(null)
    window.localStorage.removeItem('jwt')
    this.user = null
    store.favoriteAuctionsStore.favoriteRegistry.clear()
    this.setError('')
  }

  deleteUser = async (email: string) => {
    this.loading = true
    try {
      await agent.Account.delete(email)
      runInAction(() => {
        this.userRegistry.delete(email)
        this.loading = false
      })
    } catch (error) {
      console.log(error)
      runInAction(() => {
        this.loading = false
      })
    }
  }

  updateUserRole = async (user: User, roles: string[]) => {
    this.loadingButton = true
    const oldRole = user.userRoles
    user.userRoles = roles
    try {
      await agent.Account.edit(user)
      if (roles.length) {
        runInAction(() => {
          this.userRegistry.set(user.email, user)
          this.loadingButton = false
        })
        toast.success('User Role Updated Successfuly')
      } else {
        user.userRoles = oldRole
      }
    } catch (error: any) {
      runInAction(() => {
        this.loadingButton = false
      })
      throw error
    }
  }
}
